import { Fragment, useCallback, useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import { IPedOverlayValue, PedOverlaySection } from '~/shared';
import { SelectItem } from '~/components/ui/SelectItem';
import {
	PropertyContainer,
	PropertyContainerRow,
} from '~/components/ui/PropertyContainer';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { SelectColor } from '~/components/ui/SelectColor';
import {
	setOverlayValues,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { SectionHeading } from '../SectionHeading';
import { Section } from '../Section';
import { facialHairIcons, facialHairSections } from './FacialHair.model';
import { StyledSettingsContainer } from './FacialHairTab.styled';

const FacialHairTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const overlaySectionConfigs = useObservable(
		rage.characterCreation.getOverlaySectionConfigs(),
	);
	const overlayConfigs = useLayoutSelector(
		(state) => state.characterCreation.overlayConfigs,
	);
	const overlayValues = useLayoutSelector(
		(state) => state.characterCreation.overlayValues,
	);
	const hairColors = useObservable(rage.characterCreation.getHairColors());

	const filteredOverlayConfigs = useMemo(
		() =>
			overlayConfigs.filter((item) =>
				facialHairSections.includes(item.section),
			),
		[overlayConfigs],
	);

	const handleChangeHair = useCallback(
		async (section: PedOverlaySection, index: number) => {
			const valueIndex = overlayValues.findIndex(
				(element) => element.section === section,
			);
			if (valueIndex === -1) {
				return;
			}

			const newValue = { ...overlayValues[valueIndex], index };

			const newValues = overlayValues.map((item, i) =>
				i === valueIndex ? newValue : item,
			);

			dispatch(setOverlayValues(newValues));
			await rage.characterCreation.setOverlayValues([newValue]);
		},
		[overlayValues],
	);

	const handleChangeColor = useCallback(
		(field: keyof IPedOverlayValue, section: PedOverlaySection) =>
			async (value: string) => {
				const valueIndex = overlayValues.findIndex(
					(element) => element.section === section,
				);
				if (valueIndex === -1) {
					return;
				}

				const selectedColorIndex = hairColors.find(
					(color) => color.hex === value,
				)?.index;

				const newValue = {
					...overlayValues[valueIndex],
					[field]: selectedColorIndex,
				};

				const newValues = overlayValues.map((item, i) =>
					i === valueIndex ? newValue : item,
				);

				dispatch(setOverlayValues(newValues));
				await rage.characterCreation.setOverlayValues([newValue]);
			},
		[overlayValues, hairColors],
	);

	const handleChangeOpacity = useCallback(
		(section: PedOverlaySection) => async (value: number) => {
			const valueIndex = overlayValues.findIndex(
				(element) => element.section === section,
			);
			if (valueIndex === -1) {
				return;
			}

			const newValue = { ...overlayValues[valueIndex], colorOpacity: value };

			const newValues = overlayValues.map((item, i) =>
				i === valueIndex ? newValue : item,
			);

			dispatch(setOverlayValues(newValues));
			await rage.characterCreation.setOverlayValues([newValue]);
		},
		[overlayValues],
	);

	return (
		<StyledSettingsContainer>
			{facialHairSections.map((section, sectionIndex) => {
				const items = filteredOverlayConfigs.filter(
					(item) => item.section === section,
				);
				if (!items.length) {
					return null;
				}
				const value = overlayValues.find((value) => value.section === section);
				const hairValue = items.find((item) => item.index === value?.index);
				const overlaySectionConfig = overlaySectionConfigs.find(
					(config) => config.section === section,
				);
				const colorPrimary = hairColors.find(
					(color) => color.index === value?.colorPrimary,
				);
				const colorSecondary = hairColors.find(
					(color) => color.index === value?.colorSecondary,
				);

				const selectItemValue = hairValue?.name
					? {
							value: hairValue.index,
							title: hairValue.name,
					  }
					: {
							value: items[0].index,
							title: items[0].name,
					  };

				return (
					<Section key={sectionIndex}>
						<SectionHeading
							icon={facialHairIcons[section] || ''}
							text={overlaySectionConfig?.name || ''}
						/>
						<PropertyContainer>
							<PropertyContainerRow>
								<SelectItem
									value={selectItemValue}
									items={items.map((hair) => ({
										value: hair.index,
										title: hair.name,
									}))}
									onChange={(value) => handleChangeHair(section, value.value)}
									fullWidth
									count
									loop
								/>
							</PropertyContainerRow>
							{overlaySectionConfig?.withColor && (
								<Fragment>
									<PropertyContainerRow>
										<Text>Основной цвет</Text>
										<SelectColor
											value={colorPrimary?.hex}
											items={hairColors.map((item) => item.hex)}
											onChange={handleChangeColor('colorPrimary', section)}
										/>
									</PropertyContainerRow>
									<PropertyContainerRow>
										<Text>Доп. цвет</Text>
										<SelectColor
											value={colorSecondary?.hex}
											items={hairColors.map((item) => item.hex)}
											onChange={handleChangeColor('colorSecondary', section)}
										/>
									</PropertyContainerRow>
								</Fragment>
							)}
							{overlaySectionConfig?.withOpacity && (
								<PropertyContainerRow>
									<Text>Насыщенность</Text>
									<Slider
										min={0}
										max={1}
										value={value?.colorOpacity || 0}
										onChange={handleChangeOpacity(section)}
									/>
								</PropertyContainerRow>
							)}
						</PropertyContainer>
					</Section>
				);
			})}
		</StyledSettingsContainer>
	);
};

export { FacialHairTab };
